import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../api/dashboard';

export function SchemePipeline() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'scheme-pipeline'],
    queryFn: dashboardApi.schemePipeline,
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return (
      <div className="bg-white border border-cream-200 rounded-sm">
        <div className="px-5 py-4 border-b border-cream-100">
          <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">
            Scheme Pipeline · FY 2025-26
          </h3>
        </div>
        <div className="p-5 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 bg-cream-100 rounded-sm animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-cream-200 rounded-sm">
      <div className="px-5 py-4 border-b border-cream-100">
        <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">
          Scheme Pipeline · FY 2025-26
        </h3>
        <p className="text-xs text-navy-800/60 mt-3">
          Goat/Sheep/Pig units verified vs target · Poultry by approval stage
        </p>
      </div>
      <div className="p-5 space-y-4">
        {data.schemes.map((s) => {
          const vp = Math.min(100, (s.verified / s.target) * 100);
          const rp = Math.min(100 - vp, (s.subsidy_released / s.target) * 100);
          const pp = Math.min(100 - vp - rp, (s.pipeline / s.target) * 100);
          return (
            <div key={s.code} className="rounded-sm -mx-2 px-2 py-1">
              <div className="flex items-baseline justify-between mb-2">
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-medium text-navy-800">{s.name}</span>
                  <span className="text-[11px] text-navy-800/50 font-numeric">
                    Target {s.target.toLocaleString('en-IN')} · ₹{(s.unit_cost_inr / 1000).toFixed(0)}K/unit
                  </span>
                </div>
                <span className="text-sm font-numeric text-navy-800 font-medium">
                  {s.verified.toLocaleString('en-IN')} / {s.target.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="relative h-7 bg-cream-100 rounded-sm overflow-hidden flex">
                <div className="bg-navy-800 h-full flex items-center justify-end px-2" style={{ width: `${vp}%` }}>
                  {vp > 8 && <span className="text-[10px] text-white font-numeric">{vp.toFixed(1)}%</span>}
                </div>
                <div className="bg-navy-500 h-full" style={{ width: `${rp}%` }} />
                <div className="bg-navy-200 h-full" style={{ width: `${pp}%` }} />
              </div>
              <div className="flex items-center gap-4 mt-2 text-[11px] font-numeric text-navy-800/70">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-navy-800" />Verified {s.verified.toLocaleString('en-IN')}</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-navy-500" />Subsidy {s.subsidy_released}</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-navy-200" />Pipeline {s.pipeline}</span>
                <span className="ml-auto text-saffron-600 font-medium">₹{s.disbursed_cr.toFixed(1)} Cr</span>
              </div>
            </div>
          );
        })}

        <div className="rounded-sm -mx-2 px-2 py-1">
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-baseline gap-3">
              <span className="text-sm font-medium text-navy-800">Poultry Investments</span>
              <span className="text-[11px] text-navy-800/50 font-numeric">DPR-driven · Multi-stage approval</span>
            </div>
            <span className="text-sm font-numeric text-navy-800 font-medium">
              {Object.values(data.poultry).reduce((a, b) => a + b, 0)} active
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-numeric">
            <span className="px-2 py-1 bg-cream-100 text-navy-800/70 rounded-sm">CVO · {data.poultry.cvo_review}</span>
            <span className="text-cream-300">→</span>
            <span className="px-2 py-1 bg-cream-100 text-navy-800/70 rounded-sm">Pre-appraisal · {data.poultry.pre_appraisal}</span>
            <span className="text-cream-300">→</span>
            <span className="px-2 py-1 bg-saffron-100 text-saffron-700 rounded-sm">Appraisal · {data.poultry.appraisal}</span>
            <span className="text-cream-300">→</span>
            <span className="px-2 py-1 bg-navy-100 text-navy-800 rounded-sm">Final · {data.poultry.final_approval}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
